import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { isLegacyPath } from "./legacy-routes";

export { useParams } from "react-router-dom";

// Thrown from pages and caught by the site error boundary, like Next.js.
export class NotFoundError extends Error {
  constructor() {
    super("NEXT_NOT_FOUND");
  }
}

export class RedirectError extends Error {
  constructor(readonly url: string) {
    super("NEXT_REDIRECT");
  }
}

export function notFound(): never {
  throw new NotFoundError();
}

export function redirect(url: string): never {
  throw new RedirectError(url);
}

export function permanentRedirect(url: string): never {
  throw new RedirectError(url);
}

export function usePathname() {
  return useLocation().pathname;
}

export function useSearchParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function useRouter() {
  const navigate = useNavigate();

  return useMemo(() => {
    const go = (href: string, replace: boolean) => {
      if (isLegacyPath(href) || /^[a-z][a-z\d+.-]*:/i.test(href)) {
        window.location[replace ? "replace" : "assign"](href);
      } else {
        navigate(href, { replace });
      }
    };

    return {
      push: (href: string) => go(href, false),
      replace: (href: string) => go(href, true),
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => window.location.reload(),
      prefetch: () => undefined,
    };
  }, [navigate]);
}
